import { logger } from '@logger'

const MODULE = 'Communication Bridge'

/**
 * Generic parent⇄child (iframe) postMessage transport.
 *
 * Protocol-agnostic on purpose: each integration (canvas, chat, …) registers a
 * "connection" carrying its OWN `match`/`parse`/`build` from a contract, so many
 * iframes can share ONE window listener without cross-talk. Inbound messages are
 * routed to the first connection whose `match(data)` claims them; everything
 * else is ignored. Sends target the exact allowed/pinned origin, never '*'
 * unless the integration explicitly opted into it.
 *
 * The handshake choreography (who greets whom) is NOT encoded here — it lives in
 * each integration's `onMessage`. This module only routes and delivers.
 */
class CommunicationBridge {
  constructor() {
    this.connections = new Map()
    this.listening = false
    this._onMessage = this._onMessage.bind(this)
  }

  /**
   * Register (or replace) a connection.
   * @param {object} config
   * @param {string} config.id                unique key, e.g. 'canvas' | 'chat'
   * @param {() => Window|null} config.getContentWindow  the iframe's contentWindow (lazy)
   * @param {string} [config.allowedOrigin]   exact origin for inbound checks + sends ('' or '*' = skip)
   * @param {(data:any)=>boolean} config.match      does this inbound message belong to me?
   * @param {(data:any)=>({type:string,payload:any}|null)} config.parse
   * @param {(type:string,payload:any)=>object} config.build   outbound envelope builder
   * @param {(type:string,payload:any,meta:object)=>void} config.onMessage
   */
  connect(config) {
    if (!config?.id) {
      logger.warn(MODULE, 'connect() ignored — missing connection id')
      return
    }
    this.connections.set(config.id, { peerWindow: null, peerOrigin: null, ...config })
    this._ensureListening()
    logger.info(MODULE, `Connected "${config.id}"`)
  }

  disconnect(id) {
    if (this.connections.delete(id)) logger.info(MODULE, `Disconnected "${id}"`)
    if (this.connections.size === 0) this._stopListening()
  }

  // True once we've received (and pinned) at least one message from the child.
  isConnected(id) {
    return this.connections.get(id)?.peerWindow != null
  }

  /**
   * Build via the connection's contract and post to the child frame. Returns
   * false (and no-ops) when there's no live connection/target — callers can fire
   * best-effort without guarding.
   */
  send(id, type, payload = {}) {
    const conn = this.connections.get(id)
    if (!conn) {
      logger.warn(MODULE, `send() ignored — no connection "${id}"`)
      return false
    }

    const target = conn.peerWindow || conn.getContentWindow?.()
    if (!target) {
      logger.warn(MODULE, `send() ignored — no target window for "${id}"`)
      return false
    }

    const origin = conn.peerOrigin || conn.allowedOrigin || '*'
    try {
      target.postMessage(conn.build(type, payload), origin)
      return true
    } catch (err) {
      logger.error(MODULE, `send() failed for "${id}"`, err)
      return false
    }
  }

  _ensureListening() {
    if (this.listening) return
    window.addEventListener('message', this._onMessage)
    this.listening = true
  }

  _stopListening() {
    if (!this.listening) return
    window.removeEventListener('message', this._onMessage)
    this.listening = false
  }

  _onMessage(event) {
    const data = event?.data
    if (!data || typeof data !== 'object') return

    // Route to the first connection that claims this message.
    let conn = null
    for (const candidate of this.connections.values()) {
      if (candidate.match?.(data)) {
        conn = candidate
        break
      }
    }
    if (!conn) return // foreign traffic (other iframe / extension / devtools)

    // Origin allowlist — skipped only when the integration opted into '*'.
    if (conn.allowedOrigin && conn.allowedOrigin !== '*' && event.origin !== conn.allowedOrigin) {
      logger.warn(MODULE, `Dropped "${conn.id}" message from disallowed origin`, event.origin)
      return
    }

    const parsed = conn.parse?.(data)
    if (!parsed) return

    // Pin the peer so subsequent sends hit the exact frame + origin.
    conn.peerWindow = event.source
    if (event.origin && event.origin !== 'null') conn.peerOrigin = event.origin

    conn.onMessage?.(parsed.type, parsed.payload, { origin: event.origin, source: event.source })
  }
}

export const communicationBridge = new CommunicationBridge()
